import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import HeaderLogo from "../components/HeaderLogo";
import PageName from "../components/PageName";
import Svg from "../components/figs/Svg";
import { useForm } from "react-hook-form";
import { CropImage } from "../components/CropImage";
import Loading from "../components/Loading";
import { checkImageDataType, fetchAuthPost } from "../functions/functions";
import api from "../routes/api";
import { useConfirmModal } from "../hooks/useConfirmModal";
import { useAlertModal } from "../hooks/useAlertModal";
import MultiSelect from "../components/MultiSelect";
import { useNavigate } from "react-router";
import ButtonBack from "../components/ButtonBack";
import { useContentModal } from "../hooks/useContentModal";

export default function BoatSettings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { msg } = useSelector((state) => state.language);
  const [boatTypeList, setBoatTypeList] = useState([]);
  const [portList, setPortList] = useState([]);
  const [isAddNew, setIsAddNew] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(true);
  const [srcImage, setSrcImage] = useState(null);
  const [boatList, setBoatList] = useState([]);
  const [filterBoatList, setFilterBoatList] = useState([]);
  const [showButtonBack, setShowButtonBack] = useState(false);
  const { confirm, ConfirmModal } = useConfirmModal();
  const { alert, AlertModal } = useAlertModal();
  const { setContent, setTitle, handleOpen, handleClose, ContentModal } =
    useContentModal();
  const {
    register,
    setValue,
    trigger,
    handleSubmit,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm();
  const image = watch("image");
  const id_boat_type = watch("id_boat_type");
  const id_port = watch("id_port");

  const onAddNew = () => {
    setShowButtonBack(true);
    setIsAddNew(true);
    setIsEdit(false);
    setIsView(false);
    reset({});
    setSrcImage(null);
  };

  const onAddNewBoatType = async (data) => {
    try {
      await fetchAuthPost(api.setting.createBoatType, {
        data: { boat_type: data },
      });
      const responseBoatTypeList = await fetchAuthPost(
        api.setting.readBoatTypeList
      );
      setBoatTypeList(responseBoatTypeList.data);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const onAddNewPort = async (data) => {
    try {
      await fetchAuthPost(api.setting.createPort, {
        data: { port: data },
      });
      const responsePortList = await fetchAuthPost(api.setting.readPortList);
      setPortList(responsePortList.data);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const onSubmit = async (data) => {
    const imageDataType = checkImageDataType({ src: data.image });
    let imageSrc = null;
    if (imageDataType === "url") {
      const result = await fetchAuthPost(api.utils.getImageUriByImageUrlInGcs, {
        imageUrl: data.image,
      });
      imageSrc = result.data;
    } else if (imageDataType === "base64") {
      imageSrc = data.image;
    }
    let payload = {
      boat_id: data.boat_id || null,
      boat_short_name: data.boat_short_name || null,
      boat_name: data.boat_name || null,
      id_boat_type: data.id_boat_type[0] || null,
      registration_no: data.registration_no || null,
      passenger_capacity_registration:
        data.passenger_capacity_registration || null,
      passenger_capacity_company_policy:
        data.passenger_capacity_company_policy || null,
      cabin: data.cabin || null,
      boat_size: data.boat_size || null,
      year: data.year || null,
      refit_year: data.refit_year || null,
      max_speed: data.max_speed || null,
      cruising_speed: data.cruising_speed || null,
      id_port: data.id_port[0] || null,
      guest_day: data.guest_day || null,
      guest_sleeping: data.guest_sleeping || null,
      image: imageSrc,
    };
    const payloadCreate = payload;
    const payloadUpdate = { ...payload, id_boat: data.id_boat };
    try {
      setIsLoading(true);
      let response = null;
      if (data.id_boat) {
        response = await fetchAuthPost(api.setting.updateBoat, {
          data: payloadUpdate,
        });
        if (response.status === "error") {
          return alert(msg.alert, response.msg);
        }
      } else {
        response = await fetchAuthPost(api.setting.createBoat, {
          data: payloadCreate,
        });
        if (response.status === "error") {
          return alert(msg.alert, response.msg);
        }
      }
      if (response.status === "success") {
        response = await fetchAuthPost(api.setting.readBoatList);
        setIsAddNew(false);
        setIsEdit(false);
        reset({});
        setSrcImage(null);
        setBoatList(response.data);
        setShowButtonBack(false);
      }
    } catch (error) {
      console.error(error);
      alert(msg.alert, error.msg || msg.try_again);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitImage = (src) => {
    setSrcImage(src);
    setValue("image", src);
    trigger("image");
    if (src) {
      handleClose();
    }
  };

  const onEdit = (data) => {
    setShowButtonBack(true);
    setIsEdit(true);
    setIsView(false);
    reset(data);
  };

  const onView = (data) => {
    setShowButtonBack(true);
    setIsEdit(true);
    setIsView(true);
    reset(data);
  };

  const onCancel = () => {
    setShowButtonBack(false);
    setIsAddNew(false);
    setIsEdit(false);
    reset({});
    setSrcImage(null);
    setFilterBoatList([]);
  };

  const onDelete = async (id_boat) => {
    try {
      const isConfirmDelete = await confirm(
        msg.delete_boat,
        msg.are_you_sure_want_to_delete_this_boat
      );
      if (isConfirmDelete) {
        setIsLoading(true);
        const response = await fetchAuthPost(api.setting.deleteBoat, {
          data: { id_boat },
        });
        if (response.status === "error") {
          throw response;
        }
        const responseBoatList = await fetchAuthPost(api.setting.readBoatList);
        setBoatList(responseBoatList.data);
        setFilterBoatList([]);
      }
    } catch (error) {
      console.error(error);
      alert(msg.alert, error.msg);
    } finally {
      setIsLoading(false);
    }
  };

  const onFilter = (e) => {
    const value = e.target.value;
    if (value.length > 0) {
      const temp = boatList.filter((el) => {
        return (
          `${el.boat_id}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.boat_name}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.boat_short_name}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.registration_no}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.passenger_capacity_registration}`
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          `${el.passenger_capacity_company_policy}`
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          `${el.cabin}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.boat_size}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.year}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.refit_year}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.max_speed}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.cruising_speed}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.guest_day}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.guest_sleeping}`.toLowerCase().includes(value.toLowerCase()) ||
          `${el.port}`.toLowerCase().includes(value.toLowerCase())
        );
      });
      setFilterBoatList(temp);
    } else {
      setFilterBoatList([]);
    }
  };
  const initFetch = async () => {
    try {
      setIsLoading(true);
      const readBoatTypeList = async () => {
        const response = await fetchAuthPost(api.setting.readBoatTypeList);
        if (response.status === "error") {
          throw response;
        }
        setBoatTypeList(response.data);
      };
      await readBoatTypeList();

      const readPortList = async () => {
        const response = await fetchAuthPost(api.setting.readPortList);
        if (response.status === "error") {
          throw response;
        }
        setPortList(response.data);
      };
      await readPortList();

      const readBoatList = async () => {
        const response = await fetchAuthPost(api.setting.readBoatList);
        if (response.status === "error") {
          throw response;
        }
        setBoatList(response.data);
      };
      await readBoatList();
    } catch (error) {
      console.error(error);
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    initFetch();
  }, []);

  useEffect(() => {
    if (image) {
      setSrcImage(image);
    }
  }, [srcImage]);
  useEffect(() => {
    if (id_boat_type?.length > 0) {
      trigger("id_boat_type");
    }
  }, [id_boat_type]);
  useEffect(() => {
    if (id_port?.length > 0) {
      trigger("id_port");
    }
  }, [id_port]);

  // boat_id
  // boat_short_name
  // boat_name
  // id_boat_type
  // registration_no
  // passenger_capacity_registration
  // passenger_capacity_company_policy
  // cabin
  // boat_size
  // year
  // refit_year
  // max_speed
  // cruising_speed
  // id_port
  // guest_day
  // guest_sleeping
  // image
  useEffect(() => {
    if (boatTypeList.length > 0 && portList.length > 0 && boatList.length > 0) {
      setIsLoading(false);
    }
  }, [boatTypeList, boatList, portList]);
  if (isLoading) return <Loading />;
  return (
    <div className="page_boat_settings">
      <ButtonBack
        hidden={!showButtonBack}
        onClick={() => {
          onCancel();
        }}
      />
      <HeaderLogo />
      <PageName titleName={msg.boat_settings} />
      {isAddNew === false && isEdit === false && (
        <div className="data_container">
          <div className="data_card_list_header">
            <span className="left_at_header">{msg.boat_list}</span>
            <span className="right_at_header">
              <span>
                <Svg iconName="search" className="size-4" />
                <input onChange={onFilter} placeholder={msg.search} />
              </span>
              <span>
                <button onClick={onAddNew}>{msg.add_new}</button>
              </span>
            </span>
          </div>
          <div className="data_card_list_body">
            {(
              (filterBoatList.length > 0 ? filterBoatList : boatList) || []
            ).map((el) => (
              <BoatCard
                key={el.id_boat}
                data={{
                  ...el,
                  boat_type: boatTypeList.find(
                    (el2) => +el2.id_boat_type === +el.id_boat_type
                  )?.boat_type,
                  port: portList.find((el2) => +el2.id_port === +el.id_port)
                    ?.port,
                }}
                onEdit={() => onEdit(el)}
                onView={() => onView(el)}
                onDelete={() => onDelete(el.id_boat)}
              />
            ))}
          </div>
        </div>
      )}
      {(isAddNew === true || isEdit === true) && (
        <div className="boat_settings">
          <div className="_title_name_form">{msg.boat_information}</div>
          <div className="boat_form_container">
            <div className="form">
              <div className="_input_text_select">
                <label>{`${msg.boat_id} *`}</label>
                <input
                  placeholder={msg.boat_id}
                  {...register("boat_id", {
                    required: true,
                  })}
                  className={errors.boat_id ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.boat_short_name} *`}</label>
                <input
                  placeholder={msg.boat_short_name}
                  {...register("boat_short_name", {
                    required: true,
                  })}
                  className={errors.boat_short_name ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.boat_name} *`}</label>
                <input
                  placeholder={msg.boat_name}
                  {...register("boat_name", {
                    required: true,
                  })}
                  className={errors.boat_name ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.boat_type} *`}</label>
                <input
                  type="hidden"
                  {...register("id_boat_type", { required: true })}
                />
                <MultiSelect
                  className={errors.id_boat_type ? "_data_invalid" : ""}
                  disabled={isView}
                  placeholder={msg.boat_type}
                  onSaveNewOption={onAddNewBoatType}
                  options={boatTypeList.map((el) => ({
                    value: +el.id_boat_type,
                    label: el.boat_type,
                  }))}
                  multiple={false}
                  onSelectOption={(data) => {
                    setValue(
                      "id_boat_type",
                      data.map((el) => el.value)
                    );
                  }}
                  selectedOption={boatTypeList
                    .filter((el) => +el.id_boat_type === +id_boat_type)
                    .map((el) => ({
                      value: +el.id_boat_type,
                      label: el.boat_type,
                    }))}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.registration_no} *`}</label>
                <input
                  placeholder={msg.registration_no}
                  {...register("registration_no", {
                    required: true,
                  })}
                  className={errors.registration_no ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.passenger_capacity} * (${msg.registration})`}</label>
                <input
                  placeholder={msg.passenger_capacity}
                  {...register("passenger_capacity_registration", {
                    required: true,
                  })}
                  className={
                    errors.passenger_capacity_registration
                      ? "_data_invalid"
                      : ""
                  }
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.passenger_capacity} * (${msg.company_policy})`}</label>
                <input
                  placeholder={msg.passenger_capacity}
                  {...register("passenger_capacity_company_policy", {
                    required: true,
                  })}
                  className={
                    errors.passenger_capacity_company_policy
                      ? "_data_invalid"
                      : ""
                  }
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.cabin}`}</label>
                <input
                  placeholder={msg.rooms}
                  {...register("cabin")}
                  className={errors.cabin ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg.rooms}</p>
              </div>
              <div className="_input_text_select">
                <label>{`${msg.boat_size}`}</label>
                <input
                  placeholder={msg.boat_size}
                  {...register("boat_size")}
                  className={errors.boat_size ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg["sq.ft"]}</p>
              </div>
              <div className="_input_text_select">
                <label>{`${msg.year}`}</label>
                <input
                  placeholder={msg.year}
                  {...register("year")}
                  className={errors.year ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.refit_year}`}</label>
                <input
                  placeholder={msg.refit_year}
                  {...register("refit_year")}
                  className={errors.refit_year ? "_data_invalid" : ""}
                  disabled={isView}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.max_speed}`}</label>
                <input
                  placeholder={msg.knots}
                  {...register("max_speed")}
                  className={errors.max_speed ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg.knots}</p>
              </div>
              <div className="_input_text_select">
                <label>{`${msg.cruising_speed}`}</label>
                <input
                  placeholder={msg.knots}
                  {...register("cruising_speed")}
                  className={errors.cruising_speed ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg.knots}</p>
              </div>
              <div className="_input_text_select">
                <label>{`${msg.port} *`}</label>
                <input
                  type="hidden"
                  {...register("id_port", { required: true })}
                />
                <MultiSelect
                  className={errors.id_port ? "_data_invalid" : ""}
                  disabled={isView}
                  placeholder={msg.port}
                  onSaveNewOption={onAddNewPort}
                  options={portList.map((el) => ({
                    value: +el.id_port,
                    label: el.port,
                  }))}
                  multiple={false}
                  onSelectOption={(data) => {
                    setValue(
                      "id_port",
                      data.map((el) => el.value)
                    );
                  }}
                  selectedOption={portList
                    .filter((el) => +el.id_port === +id_port)
                    .map((el) => ({ value: +el.id_port, label: el.port }))}
                />
              </div>
              <div className="_input_text_select">
                <label>{`${msg.guest_day}`}</label>
                <input
                  placeholder={msg.pax}
                  {...register("guest_day")}
                  className={errors.guest_day ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg.pax}</p>
              </div>
              <div className="_input_text_select">
                <label>{`${msg.guest_sleeping}`}</label>
                <input
                  placeholder={msg.pax}
                  {...register("guest_sleeping")}
                  className={errors.guest_sleeping ? "_data_invalid" : ""}
                  disabled={isView}
                />
                <p className="unit">{msg.pax}</p>
              </div>
            </div>
            <input type="hidden" {...register("image")} />
            <div className="boat_form_image">
              <div className="_input_text_select">
                <div
                  className="boat_button_upload_image"
                  onClick={() => {
                    if (isView) return;
                    setTitle(msg.upload_image);
                    setContent(
                      <div style={{ padding: 20 }}>
                        <CropImage
                          setSrc={(src) => onSubmitImage(src.src)}
                          existingImage={image}
                        />
                      </div>
                    );
                    handleOpen();
                  }}
                >
                  {image ? (
                    <img src={image} alt="boat_image" />
                  ) : (
                    <>
                      <Svg iconName="empty_image" />
                      <span>{msg.upload_image}</span>
                    </>
                  )}
                </div>
              </div>
              <div>
                {isView ? (
                  <button
                    onClick={() => setIsView(false)}
                    className="_fill_button"
                  >
                    {msg.edit}
                  </button>
                ) : (
                  <>
                    <button className="_outline_button" onClick={onCancel}>
                      {msg.cancel}
                    </button>
                    <button
                      onClick={handleSubmit(onSubmit)}
                      className="_fill_button"
                    >
                      {getValues("id_boat") ? msg.update : msg.save}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {ConfirmModal}
      {AlertModal}
      {ContentModal}
    </div>
  );
}

const BoatCard = ({ data, onEdit, onView, onDelete }) => {
  const { msg } = useSelector((s) => s.language);
  return (
    <div className="boat_card">
      <div onClick={onView} className="boat_card_image">
        {data.image ? (
          <img src={data.image} alt={data.boat_name} />
        ) : (
          <Svg iconName="empty_image" />
        )}
        <label>{data.boat_type}</label>
      </div>
      <div className="boat_card_info">
        <h2>{data.boat_name}</h2>
        <p>
          <span>{msg.boat_id}</span>
          <span>{data.boat_id}</span>
        </p>
        <p>
          <span>{msg.registration_number}</span>
          <span>{data.registration_no}</span>
        </p>
        <p>
          <span>{msg.passenger_capacity}</span>
          <span>
            {data.passenger_capacity_company_policy ||
              data.passenger_capacity_registration}
            {` ${msg.pax}`}
          </span>
        </p>
        <p>
          <span>{msg.port}</span>
          <span>{data.port}</span>
        </p>
        <div>
          <button onClick={onEdit}>
            <Svg iconName="edit" />
            {msg.edit}
          </button>
          <div></div>
          <button onClick={onDelete}>
            <Svg iconName="delete" />
            {msg.delete}
          </button>
        </div>
      </div>
    </div>
  );
};
